/* dm-native-options.js: native option forms for products that used the Globo app
 * (Globo rebuild, 27 Oct 2026). Loaded only on those products by
 * snippets/dm-native-options.liquid, which also writes the config
 * (snippets/dm-native-cfg-*.liquid). Every price in the config is read live in Liquid.
 *
 * Wired like the pebble picture builder (assets/daisy-pebble-picture.js): mounts
 * above the product form, takes over Add to Cart (and the sticky button), checks
 * the required fields, adds the main line plus its helper lines in one
 * /cart/add.js request, keeps a running total, then goes to the basket.
 *
 * Config: { title, variantId, price, currency, mainName?, sections: [section],
 *           offer?: { key, yes, no, name, label ("{price}" = live price), variantId, price, available, sections } }
 * section: { title, showIf?, fields: [field] }
 * field:   { id, key (cart property), label, type: "choice" | "select" | "text"
 *            | "textarea" | "checkbox" | "file",
 *            required?, placeholder?, max?, default?, hideLabel?, sep?, errorLabel?, help?,
 *            showIf?, options?: [ "value" | { value, showIf?, addon?: { handle?, variantId, price, available } } ] }
 * showIf:  { field: id, in?: [values], min?: n, max?: n }; shown when that field is
 *          shown and has a value that passes (no in/min/max = any value).
 * A chosen option with an addon adds one line of that helper variant.
 *
 * Added for the wedding stationery (27 Oct 2026); none of these change the types above:
 *  - textarea: multi-line text; the line breaks are kept in the cart property.
 *  - checkbox: one tick box; options[0] is its value (sent only when ticked) and may
 *    carry an addon, like a priced choice. An empty label shows the box on its own.
 *  - file: { accept: ".doc,.docx,..." }. Main item only. When a file is chosen the
 *    main line is added as multipart (properties[<key>] = the file, as the Order of
 *    the Day builder does), then the helper lines as JSON; if that second request
 *    fails the main line is taken out again.
 *  - help: a line of help text under the control.
 *  - addon.handle: if Liquid could not read the helper (variantId null, e.g. over
 *    Shopify's 20-handle all_products limit), its live price/variant is read from
 *    /products/<handle>.js before the form is drawn. Configs without handles are
 *    untouched and start exactly as before.
 *
 * Added for the garden signs, bottle openers and tea light (27 Oct 2026, batch 2); same rule,
 * configs without these properties render and behave exactly as before:
 *  - transform: "uppercase" (text): shown in capitals and sent in capitals, as Globo did.
 *  - image: { src, alt } on any field: a small picture above it (tea light matchbox add-on).
 *  - offer.handle: like addon.handle, for the second-item offer helper.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-dm-nopt]');
  if (!root || root.hasAttribute('data-dm-nopt-ready')) return;
  var cfg;
  try { cfg = JSON.parse(root.querySelector('[data-dm-nopt-config]').textContent); } catch (e) { return; }
  root.setAttribute('data-dm-nopt-ready', '');

  var ATC = '#product-add-to-cart,.dm-sticky-btn,[data-sticky-add-to-cart] button[name="add"]';
  var GLOBO = '.gpo-product-variants,.gpo-container,.gpo-app,.gpo-options,.globo-product-options,.globo-options,[data-gpo],[data-globo]';
  var busy = false;
  var validationShown = false; // the error line is showing a "Please complete" message
  var entries = []; // one per field, in page order: { f, scope, sec, visible, value, opt }
  var byId = {};

  function qs(sel, el) { return (el || document).querySelector(sel); }
  function qsa(sel, el) { return Array.prototype.slice.call((el || document).querySelectorAll(sel)); }
  function clean(v) { return String(v == null ? '' : v).replace(/\s+/g, ' ').trim(); }
  // Multi-line text: tidy each line, keep the line breaks.
  function cleanLines(v) {
    return String(v == null ? '' : v).replace(/\r\n?/g, '\n').split('\n')
      .map(function (l) { return l.replace(/\s+/g, ' ').trim(); }).join('\n')
      .replace(/^\n+|\n+$/g, '');
  }
  var CONTROLS = 'select,input[type="text"],textarea,input[type="file"],input[type="checkbox"]';
  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(cents) {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: cfg.currency || 'GBP' }).format(cents / 100);
  }
  function short(cents) { return money(cents).replace(/\.00$/, ''); }
  function usable(addon) { return !addon || (!!addon.variantId && addon.available !== false); }

  var offer = cfg.offer && usable(cfg.offer) ? cfg.offer : null;

  // ---- config -> entries ----
  function prepare(sections, scope) {
    (sections || []).forEach(function (sec, i) {
      sec.scope = scope;
      sec.key = scope + i;
      sec.fields.forEach(function (f) {
        // An option whose helper product is gone can't be charged, so it isn't offered.
        f.opts = (f.options || []).map(function (o) { return typeof o === 'string' ? { value: o } : o; })
          .filter(function (o) { return usable(o.addon); });
        var e = { f: f, scope: scope, sec: sec, visible: false, value: '', opt: null };
        entries.push(e);
        byId[f.id] = e;
      });
    });
  }
  // Helpers Liquid could not read (variantId null but a handle given): read them
  // from /products/<handle>.js. Configs without such helpers skip this entirely.
  function unresolved() {
    var list = [];
    function walk(sections) {
      (sections || []).forEach(function (sec) {
        (sec.fields || []).forEach(function (f) {
          (f.options || []).forEach(function (o) {
            if (o && typeof o === 'object' && o.addon && o.addon.handle && !o.addon.variantId) list.push(o.addon);
          });
        });
      });
    }
    walk(cfg.sections);
    if (cfg.offer) walk(cfg.offer.sections);
    if (cfg.offer && cfg.offer.handle && !cfg.offer.variantId) list.push(cfg.offer);
    return list;
  }
  function resolveAddons(list) {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var byHandle = {};
    list.forEach(function (a) { (byHandle[a.handle] = byHandle[a.handle] || []).push(a); });
    var handles = Object.keys(byHandle);
    root.setAttribute('data-dmn-fetched', String(handles.length));
    function get(h, retry) {
      return fetch(base + 'products/' + encodeURIComponent(h) + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .catch(function (err) {
          if (!retry) throw err;
          return new Promise(function (ok) { setTimeout(ok, 1000); }).then(function () { return get(h, false); });
        });
    }
    return Promise.all(handles.map(function (h) {
      return get(h, true)
        .then(function (p) {
          if (!p || !p.variants || !p.variants.length) return;
          var v = p.variants.filter(function (x) { return x.available; })[0] || p.variants[0];
          byHandle[h].forEach(function (a) { a.variantId = v.id; a.price = v.price; a.available = v.available; });
        })
        .catch(function () {});
    }));
  }

  // ---- markup ----
  function fieldHtml(f) {
    var id = 'dmn-' + f.id;
    var star = f.required ? '&nbsp;<span class="dm-nopt__req" aria-hidden="true">*</span>' : '';
    var h = '<div class="dm-nopt__field' + (f.sep ? ' dm-nopt__field--sep' : '') + '" data-dmn-field="' + esc(f.id) + '">';
    if (f.image && f.image.src) h += '<img class="dm-nopt__img" src="' + esc(f.image.src) + '" alt="' + esc(f.image.alt || '') + '" loading="lazy" width="120" height="120">';
    if (f.type === 'choice') {
      h += '<fieldset class="dm-nopt__set"><legend class="dm-nopt__label' + (f.hideLabel ? ' dm-nopt__vh' : '') + '">' + esc(f.label) + star + '</legend><div class="dm-nopt__pills">';
      f.opts.forEach(function (o, i) {
        h += '<label class="dm-nopt__pill" data-dmn-opt="' + i + '"><input type="radio" name="' + id + '" value="' + esc(o.value) + '"' +
          (f.default === o.value ? ' checked' : '') + '><span>' + esc(o.value) + '</span>' +
          (o.addon ? '<small>+' + esc(short(o.addon.price)) + '</small>' : '') + '</label>';
      });
      return h + '</div></fieldset></div>';
    }
    var help = f.help ? '<p class="dm-nopt__help" id="' + id + '-help">' + esc(f.help) + '</p>' : '';
    if (f.type === 'checkbox') {
      var o0 = f.opts[0];
      if (f.label) h += '<p class="dm-nopt__label" id="' + id + '-label">' + esc(f.label) + star + '</p>';
      if (o0) {
        h += '<label class="dm-nopt__check" data-dmn-opt="0"><input type="checkbox" id="' + id + '" value="' + esc(o0.value) + '"' +
          (f.label ? ' aria-describedby="' + id + '-label"' : '') + '><span>' + esc(o0.value) + '</span>' +
          (o0.addon ? '<small>+' + esc(short(o0.addon.price)) + '</small>' : '') + '</label>';
      }
      return h + help + '</div>';
    }
    h += '<label class="dm-nopt__label" for="' + id + '">' + esc(f.label) + star + '</label>';
    if (f.type === 'select') {
      h += '<select id="' + id + '"><option value="">Please select</option>' + f.opts.map(function (o, i) {
        return '<option value="' + esc(o.value) + '" data-dmn-opt="' + i + '">' + esc(o.value) + (o.addon ? ' (+' + esc(short(o.addon.price)) + ')' : '') + '</option>';
      }).join('') + '</select>';
    } else if (f.type === 'textarea') {
      h += '<textarea id="' + id + '" rows="' + (f.rows || 4) + '" autocomplete="off"' + (f.max ? ' maxlength="' + f.max + '"' : '') +
        (f.placeholder ? ' placeholder="' + esc(f.placeholder) + '"' : '') + '></textarea>';
    } else if (f.type === 'file') {
      h += '<input type="file" id="' + id + '"' + (f.accept ? ' accept="' + esc(f.accept) + '"' : '') + (f.help ? ' aria-describedby="' + id + '-help"' : '') + '>';
    } else {
      h += '<input type="text" id="' + id + '"' + (f.transform === 'uppercase' ? ' class="dm-nopt__upper"' : '') + ' autocomplete="off" enterkeyhint="done"' + (f.max ? ' maxlength="' + f.max + '"' : '') +
        (f.placeholder ? ' placeholder="' + esc(f.placeholder) + '"' : '') + '>';
    }
    return h + help + '</div>';
  }
  function sectionsHtml(sections) {
    return (sections || []).map(function (sec) {
      return '<div class="dm-nopt__group" data-dmn-sec="' + sec.key + '">' +
        (sec.title ? '<h3 class="dm-nopt__title">' + esc(sec.title) + '</h3>' : '') +
        sec.fields.map(fieldHtml).join('') + '</div>';
    }).join('');
  }
  function render() {
    root.innerHTML = qs('[data-dm-nopt-config]', root).outerHTML +
      sectionsHtml(cfg.sections) +
      (offer ? '<div class="dm-nopt__group"><h3 class="dm-nopt__title">Incredible offer</h3>' +
        '<label class="dm-nopt__offer"><input type="checkbox" data-dmn-offer><span>' +
        esc(String(offer.label || '').replace('{price}', short(offer.price))) + '</span></label></div>' +
        '<div class="dm-nopt__second" data-dmn-second hidden>' + sectionsHtml(offer.sections) + '</div>' : '') +
      '<p class="dm-nopt__error" data-dmn-error role="alert" hidden></p>' +
      '<p class="dm-nopt__total">Total <strong data-dmn-total></strong></p>';
  }

  // ---- state ----
  function wrapOf(e) { return qs('[data-dmn-field="' + e.f.id + '"]', root); }
  function offerOn() { var box = qs('[data-dmn-offer]', root); return !!(offer && box && box.checked); }
  function passes(c) {
    if (!c) return true;
    var g = byId[c.field];
    if (!g || !g.visible || g.value === '') return false;
    if (c.in) return c.in.indexOf(g.value) !== -1;
    var n = parseFloat(g.value);
    if (c.min != null && !(n >= c.min)) return false;
    if (c.max != null && !(n <= c.max)) return false;
    return true;
  }
  function readValue(e, wrap) {
    if (e.f.type === 'choice') { var r = qs('input:checked', wrap); return r ? r.value : ''; }
    if (e.f.type === 'checkbox') { var b = qs('input[type="checkbox"]:checked', wrap); return b ? b.value : ''; }
    if (e.f.type === 'textarea') { var t = qs('textarea', wrap); return t ? cleanLines(t.value) : ''; }
    if (e.f.type === 'file') { var fi = qs('input[type="file"]', wrap); return fi && fi.files && fi.files.length ? clean(fi.files[0].name) : ''; }
    var input = qs('select,input', wrap);
    var v = input ? clean(input.value) : '';
    return e.f.transform === 'uppercase' ? v.toUpperCase() : v;
  }
  function update() {
    var on = offerOn();
    root.setAttribute('data-offer', on ? 'true' : 'false');
    var second = qs('[data-dmn-second]', root);
    if (second) second.hidden = !on;
    var secShown = {};
    entries.forEach(function (e) {
      var wrap = wrapOf(e);
      if (!(e.sec.key in secShown)) {
        secShown[e.sec.key] = (e.scope === 'main' || on) && passes(e.sec.showIf);
        qs('[data-dmn-sec="' + e.sec.key + '"]', root).hidden = !secShown[e.sec.key];
      }
      e.visible = secShown[e.sec.key] && passes(e.f.showIf);
      wrap.hidden = !e.visible;
      // Options with their own condition (planner sizes follow the rows).
      e.f.opts.forEach(function (o, i) {
        var node = qs('[data-dmn-opt="' + i + '"]', wrap);
        if (!node) return; // a tick box draws only its first option
        var ok = passes(o.showIf);
        var input = node.tagName === 'OPTION' ? node : qs('input', node);
        if (!ok && (input.checked || input.selected)) { input.checked = false; if (input.selected) input.parentNode.value = ''; }
        node.hidden = !ok;
        input.disabled = !ok || !e.visible;
      });
      qsa('select,input[type="text"],textarea,input[type="file"]', wrap).forEach(function (n) { n.disabled = !e.visible; });
      e.value = e.visible ? readValue(e, wrap) : '';
      e.opt = null;
      e.f.opts.forEach(function (o) { if (e.value !== '' && o.value === e.value) e.opt = o; });
      if (e.value || !e.visible) wrap.classList.remove('is-invalid');
      qsa('.dm-nopt__pill', wrap).forEach(function (p) { p.classList.toggle('is-active', qs('input', p).checked); });
      qsa('.dm-nopt__check', wrap).forEach(function (p) { p.classList.toggle('is-active', qs('input', p).checked); });
    });
    var total = cfg.price + (on ? offer.price : 0);
    entries.forEach(function (e) { if (e.visible && e.opt && e.opt.addon) total += e.opt.addon.price; });
    qs('[data-dmn-total]', root).textContent = money(total);
    qsa('.money-subtotal').forEach(function (n) { n.textContent = money(total); });
    if (validationShown && !qs('.is-invalid', root)) { validationShown = false; showError(''); }
  }

  // ---- cart ----
  function buildItems(bundle) {
    var mainProps = { '_Bundle ID': bundle, '_Custom option flow': 'Daisy native options' };
    var offerProps = { '_Bundle ID': bundle, '_Linked product': cfg.title };
    var mainAdds = [];
    var offerAdds = [];
    entries.forEach(function (e) {
      if (!e.visible || e.value === '') return;
      if (e.f.type === 'file') return; // sent as multipart by submit()
      (e.scope === 'offer' ? offerProps : mainProps)[e.f.key] = e.value;
      if (!e.opt || !e.opt.addon) return;
      var props = { '_Bundle ID': bundle, '_Linked product': cfg.title, 'Add-on': e.f.key + ': ' + e.value };
      if (offer) props.For = e.scope === 'offer' ? offer.name : cfg.mainName;
      (e.scope === 'offer' ? offerAdds : mainAdds).push({ id: e.opt.addon.variantId, quantity: 1, properties: props });
    });
    var on = offerOn();
    if (offer) mainProps[offer.key] = on ? offer.yes : offer.no;
    var items = [{ id: cfg.variantId, quantity: 1, properties: mainProps }].concat(mainAdds);
    if (on) items = items.concat([{ id: offer.variantId, quantity: 1, properties: offerProps }], offerAdds);
    return items;
  }
  // Chosen files on the main item: [{ key, file }]
  function mainFiles() {
    var out = [];
    entries.forEach(function (e) {
      if (e.f.type !== 'file' || e.scope !== 'main' || !e.visible || e.value === '') return;
      var input = qs('input[type="file"]', wrapOf(e));
      if (input && input.files && input.files[0]) out.push({ key: e.f.key, file: input.files[0] });
    });
    return out;
  }
  function fileAllowed(input, file) {
    var list = String(input.getAttribute('accept') || '').toLowerCase().split(',').map(function (x) { return x.trim(); }).filter(Boolean);
    if (!list.length) return true;
    var name = String(file.name || '').toLowerCase();
    return list.some(function (ext) { return ext.charAt(0) === '.' ? name.slice(-ext.length) === ext : true; });
  }
  function checkFile(ev) {
    var input = ev.target;
    if (!input || !input.matches || !input.matches('input[type="file"]') || !root.contains(input)) return;
    var file = input.files && input.files[0];
    if (!file || fileAllowed(input, file)) return;
    input.value = '';
    var wrap = input.closest('[data-dmn-field]');
    if (wrap) wrap.classList.add('is-invalid');
    showError('Sorry, that type of file can\'t be uploaded here. Please choose a ' + String(input.getAttribute('accept') || '').split(',').join(', ') + ' file.');
    validationShown = true;
  }
  function showError(text) {
    var node = qs('[data-dmn-error]', root);
    node.textContent = text || '';
    node.hidden = !text;
  }
  function validate() {
    var missing = entries.filter(function (e) { return e.visible && e.f.required && e.value === ''; });
    qsa('.is-invalid', root).forEach(function (n) { n.classList.remove('is-invalid'); });
    if (!missing.length) return true;
    missing.forEach(function (e) { wrapOf(e).classList.add('is-invalid'); });
    var first = missing[0];
    var owner = offerOn() ? (first.scope === 'offer' ? offer.name : cfg.mainName) : '';
    showError('Please complete: ' + (owner ? owner + ', ' : '') + (first.f.errorLabel || first.f.label) + '.');
    validationShown = true;
    var wrap = wrapOf(first);
    wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
    var focus = qs(CONTROLS, wrap);
    if (focus) setTimeout(function () { try { focus.focus({ preventScroll: true }); } catch (err) {} }, 300);
    return false;
  }
  var buttonText = [];
  function setButtons(adding) {
    qsa(ATC).forEach(function (b, i) {
      if (adding) { buttonText[i] = b.innerHTML; b.disabled = true; b.textContent = 'Adding...'; }
      else { b.disabled = false; if (buttonText[i] != null) b.innerHTML = buttonText[i]; }
    });
  }
  function submit() {
    if (busy) return;
    update();
    if (!validate()) return;
    showError('');
    busy = true;
    setButtons(true);
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    function answer(r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        if (!r.ok) throw new Error(data.description || data.message || 'Unable to add this to your basket.');
        return data;
      });
    }
    function postJson(list) {
      return fetch(base + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: list })
      }).then(answer);
    }
    var items = buildItems('native-options-' + Date.now());
    var files = mainFiles();
    var adding;
    if (!files.length) {
      adding = postJson(items);
    } else {
      // Main line with its file(s) as multipart (a file can't travel in JSON), then the rest.
      var main = items[0];
      var fd = new FormData();
      fd.append('id', main.id);
      fd.append('quantity', main.quantity);
      Object.keys(main.properties).forEach(function (k) { fd.append('properties[' + k + ']', main.properties[k]); });
      files.forEach(function (x) { fd.append('properties[' + x.key + ']', x.file, x.file.name); });
      adding = fetch(base + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, credentials: 'same-origin', body: fd })
        .then(answer)
        .then(function (line) {
          var rest = items.slice(1);
          if (!rest.length) return line;
          return postJson(rest).catch(function (err) {
            // Don't leave the board in the basket without its add-ons.
            var undo = line && line.key ? fetch(base + 'cart/change.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, credentials: 'same-origin',
              body: JSON.stringify({ id: line.key, quantity: 0 })
            }).catch(function () {}) : Promise.resolve();
            return undo.then(function () { throw err; });
          });
        });
    }
    adding.then(function () {
      qsa(ATC + ',.product-form__submit').forEach(function (b) { b.classList.add('dm-celebrate'); });
      var go = function () { window.location.href = (window.routes && window.routes.cart) || '/cart'; };
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) go(); else setTimeout(go, 800);
    }).catch(function (err) {
      busy = false;
      setButtons(false);
      showError(err.message || 'Something went wrong adding this to your basket.');
    });
  }

  // ---- mount (same placement as the pebble picture builder) ----
  function init() {
    var form = qs('product-form form[data-type="add-to-cart-form"]') || qs('form[action*="/cart/add"]');
    if (!form) return;
    render();
    var globo = qs(GLOBO);
    var target = globo && (globo.closest('.shopify-app-block') || globo);
    if (target && target.parentNode && !target.contains(form) && !form.contains(target)) target.parentNode.insertBefore(root, target);
    else form.parentNode.insertBefore(root, form);
    qsa(GLOBO).forEach(function (n) {
      var t = n.closest('.shopify-app-block') || n;
      if (t.contains(form) || form.contains(t) || t.contains(root)) return;
      t.hidden = true;
      t.style.display = 'none';
    });
    // One personalised item per Add to Cart, as on the pebble pictures.
    qsa('.productView-quantity').forEach(function (n) { n.hidden = true; });
    root.hidden = false;

    root.addEventListener('change', checkFile);
    root.addEventListener('change', update);
    root.addEventListener('input', update);
    root.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' && ev.target.matches('input[type="text"]')) { ev.preventDefault(); ev.target.blur(); }
    });
    document.addEventListener('click', function (ev) {
      if (!ev.target.closest || !ev.target.closest(ATC)) return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
      submit();
    }, true);
    document.addEventListener('submit', function (ev) {
      if (ev.target !== form) return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
      submit();
    }, true);
    // Back from the basket (page restored from cache): unlock the button again.
    window.addEventListener('pageshow', function (ev) { if (ev.persisted && busy) { busy = false; setButtons(false); } });
    update();
  }

  function boot() {
    offer = cfg.offer && usable(cfg.offer) ? cfg.offer : null; // again, after any /products/<handle>.js read
    prepare(cfg.sections, 'main');
    if (offer) prepare(offer.sections, 'offer');
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
  }
  var pending = unresolved();
  if (!pending.length) boot();
  else {
    // While the helper prices load, Add to Cart waits (so nothing is added without its options).
    var hold = function (ev) {
      var t = ev.target;
      if (ev.type === 'submit' ? t.matches && t.matches('form[action*="/cart/add"]') : t.closest && t.closest(ATC)) { ev.preventDefault(); ev.stopImmediatePropagation(); }
    };
    document.addEventListener('click', hold, true);
    document.addEventListener('submit', hold, true);
    var started = false;
    var go = function () {
      if (started) return;
      started = true;
      document.removeEventListener('click', hold, true); document.removeEventListener('submit', hold, true); boot();
      // 28 Oct 2026: never fail silently. If a helper price still could not be read, log it and tell the customer.
      var missing = unresolved();
      if (missing.length) {
        try { console.warn('dm-native-options: could not load', missing.map(function (a) { return a.handle; }).join(', ')); } catch (e) {}
        var warn = function () { if (qs('[data-dmn-error]', root)) showError('Some options could not load. Please refresh the page to see them all.'); };
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(warn, 0); });
        else setTimeout(warn, 0);
      }
    };
    resolveAddons(pending).then(go, go);
    // Don't wait for ever: after 8 s draw the form anyway (an option still without a price isn't offered).
    setTimeout(go, 8000);
  }
})();